import React, { useState, useRef, useId, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { Icon } from './Icon';
import styles from './Input.module.css';

// ── CVA definitions ──────────────────────────────────────────────────────────

const inputWrapper = cva(styles.wrapperBase, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
    status: {
      default: styles.statusDefault,
      error: styles.statusError,
      warning: styles.statusWarning,
    },
  },
  defaultVariants: {
    size: 'md',
    status: 'default',
  },
});

// ── Types ────────────────────────────────────────────────────────────────────

type InputSize = 'sm' | 'md' | 'lg';
type InputStatus = 'default' | 'error' | 'warning';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'>,
  VariantProps<typeof inputWrapper> {
  /** Input size variant */
  size?: InputSize;
  /** Validation status */
  status?: InputStatus;
  /** Icon or text before the input */
  prefix?: React.ReactNode;
  /** Icon or text after the input */
  suffix?: React.ReactNode;
  /** Addon before the input (outside border) */
  addonBefore?: React.ReactNode;
  /** Addon after the input (outside border) */
  addonAfter?: React.ReactNode;
  /** Show clear button */
  allowClear?: boolean;
  /** Show character count */
  showCount?: boolean;
  /** Disable internal focus border/shadow (when parent handles it) */
  focusEffect?: boolean;
  /** Show border (default true) */
  bordered?: boolean;
  /** Background fill variant.
   * - 'default'   — surface-container-high grey (standard form fill)
   * - 'plain'     — transparent (blends into parent surface)
   * - 'glass'     — specialized shell variant (owns its own layout)
   * - 'underline' — bottom border only, animated focus bar */
  variant?: 'default' | 'glass' | 'plain' | 'underline';
  /** Absolutely positioned element inside the glass shell (e.g. send button) */
  action?: React.ReactNode;
  /**
   * Floating label text. Sits inside the field at rest; animates upward
   * and shrinks on focus or when the field has a non-empty value.
   *
   * Works with ALL variants (default, glass, plain, underline).
   * Use `placeholder` for a native hint text when no floating label is needed.
   */
  label?: string;
}

// Map size → glass inputField size class
const glassInputFieldSizeClass: Record<InputSize, string> = {
  sm: styles.glassInputFieldSm,
  md: styles.glassInputFieldMd,
  lg: styles.glassInputFieldLg,
};

// Map size → glass affix (prefix) class
const glassAffixClass: Record<InputSize, string> = {
  sm: styles.glassAffixSm,
  md: styles.glassAffixMd,
  lg: styles.glassAffixLg,
};

// Map size → glass affix suffix class
const glassAffixSuffixClass: Record<InputSize, string> = {
  sm: styles.glassAffixSuffixSm,
  md: styles.glassAffixSuffixMd,
  lg: styles.glassAffixSuffixLg,
};

// Map size → glass size class (vertical padding)
const glassSizeClass: Record<InputSize, string> = {
  sm: styles.glassSm,
  md: styles.glassMd,
  lg: styles.glassLg,
};

/**
 * Input — text input with prefix/suffix, addons, status, and clear support.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Height and font size |
 * | status | 'default' \| 'error' \| 'warning' | 'default' | Border color |
 * | prefix | ReactNode | — | Icon/text inside input, left |
 * | suffix | ReactNode | — | Icon/text inside input, right |
 * | addonBefore | ReactNode | — | Content outside border, left |
 * | addonAfter | ReactNode | — | Content outside border, right |
 * | allowClear | boolean | false | Show X button to clear value |
 * | showCount | boolean | false | Show character count |
 * | maxLength | number | — | Maximum characters (shown with showCount) |
 *
 * @example
 * <Input label="Search" prefix={<Icon name="search" size={16} />} allowClear />
 * <Input status="error" suffix={<span>!</span>} />
 * <Input addonBefore="https://" addonAfter=".com" />
 * <Input showCount maxLength={100} />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    status = 'default',
    prefix,
    suffix,
    addonBefore,
    addonAfter,
    allowClear,
    showCount,
    focusEffect = true,
    bordered: _bordered,
    variant = 'glass',
    action,
    label,
    maxLength,
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    style,
    disabled,
    className,
    ...props
  },
  ref,
) {
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  // If the caller passed an explicit id, honor it; otherwise use the generated
  // one so the floating <label htmlFor> stays in sync with the <input id>.
  const inputId = (props as { id?: string }).id ?? generatedId;

  const isControlled = value !== undefined;
  const currentValue = isControlled ? String(value) : String(internalValue);
  const hasValue = currentValue.length > 0;
  const labelFloated = focused || hasValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) setInternalValue(e.target.value);
    onChange?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlur?.(e);
  };

  const handleClear = () => {
    if (!isControlled) setInternalValue('');
    const input = (ref as React.RefObject<HTMLInputElement>)?.current ?? inputRef.current;
    if (input) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
      nativeInputValueSetter?.call(input, '');
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const hasAddon = addonBefore || addonAfter;
  const statusIcon = status === 'error' ? 'error' : status === 'warning' ? 'warning' : null;
  const hasSuffix = !!(suffix || allowClear || showCount || action || statusIcon);

  const labelEl = label !== undefined ? (
    <label
      htmlFor={inputId}
      className={clsx(
        styles.floatingLabel,
        labelFloated ? styles.labelFloated : styles.labelRest,
        focused && styles.labelFocused,
        variant === 'glass' && styles.labelGlass,
        variant === 'plain' && styles.labelPlain,
        variant === 'underline' && styles.labelUnderline,
      )}
    >
      {label}
    </label>
  ) : null;

  // ── Glass variant ──────────────────────────────────────────────────────────
  if (variant === 'glass' && !hasAddon) {
    return (
      <div
        data-glass="true"
        className={clsx(styles.glass, glassSizeClass[size], label !== undefined && styles.glassWithLabel, className)}
        style={style}
      >
        {labelEl}
        {prefix && (
          <span className={clsx(styles.affix, glassAffixClass[size])}>
            {prefix}
          </span>
        )}
        <input
          ref={ref ?? inputRef}
          id={inputId}
          className={clsx(
            styles.inputField,
            glassInputFieldSizeClass[size],
            prefix && styles.glassInputFieldHasPrefix,
            hasSuffix && styles.glassInputFieldHasSuffix,
          )}
          value={isControlled ? value : internalValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          maxLength={maxLength}
          {...props}
        />
        {allowClear && currentValue && !disabled && (
          <button type="button" className={styles.clearBtn} onClick={handleClear} tabIndex={0} title="Clear" aria-label="Clear">
            <Icon name="close" size={14} />
          </button>
        )}
        {showCount && (
          <span className={clsx(styles.count, action ? styles.glassCountWithAction : styles.glassCountDefault)}>
            {currentValue.length}{maxLength !== undefined ? `/${maxLength}` : ''}
          </span>
        )}
        {suffix && (
          <span className={clsx(styles.affix, action ? styles.glassSuffixWithAction : styles.glassSuffixDefault, glassAffixSuffixClass[size])}>
            {suffix}
          </span>
        )}
        {statusIcon && (
          <span className={clsx(styles.affix, glassAffixSuffixClass[size], styles[`statusIcon${status === 'error' ? 'Error' : 'Warning'}`])}>
            <Icon name={statusIcon} size={16} />
          </span>
        )}
        {action && (
          <span className={styles.action}>{action}</span>
        )}
      </div>
    );
  }

  // ── Default variant ────────────────────────────────────────────────────────
  const inputField = (
    <motion.div
      className={clsx(
        inputWrapper({ size, status }),
        variant === 'plain' && styles.wrapperPlain,
        variant === 'underline' && styles.wrapperUnderline,
        variant === 'underline' && status === 'error' && styles.wrapperUnderlineError,
        variant === 'underline' && status === 'warning' && styles.wrapperUnderlineWarning,
        !focusEffect && styles.wrapperNoFocusRing,
        label !== undefined && styles.wrapperWithLabel,
        hasAddon && styles.addonInner,
        hasAddon && addonBefore && styles.addonInnerBefore,
        hasAddon && addonAfter && styles.addonInnerAfter,
        !hasAddon && className,
      )}
      style={!hasAddon ? style : undefined}
    >
      {labelEl}
      {prefix && (
        <span className={styles.affix}>{prefix}</span>
      )}
      <input
        ref={ref ?? inputRef}
        id={inputId}
        className={clsx(
          styles.inputField,
          prefix && styles.inputFieldHasPrefix,
          hasSuffix && styles.inputFieldHasSuffix,
        )}
        value={isControlled ? value : internalValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        maxLength={maxLength}
        {...props}
      />
      {allowClear && currentValue && !disabled && (
        <button type="button" className={styles.clearBtn} onClick={handleClear} tabIndex={0} title="Clear" aria-label="Clear">
          <Icon name="close" size={14} />
        </button>
      )}
      {showCount && (
        <span className={styles.count}>
          {currentValue.length}
          {maxLength !== undefined ? `/${maxLength}` : ''}
        </span>
      )}
      {suffix && (
        <span className={styles.affix}>{suffix}</span>
      )}
      {statusIcon && (
        <span className={clsx(styles.affix, styles[`statusIcon${status === 'error' ? 'Error' : 'Warning'}`])}>
          <Icon name={statusIcon} size={16} />
        </span>
      )}
    </motion.div>
  );

  if (!hasAddon) return inputField;

  return (
    <div
      className={clsx(styles.addonWrapper, className)}
      style={style}
    >
      {addonBefore && (
        <div className={clsx(styles.addon, styles.addonBefore)}>{addonBefore}</div>
      )}
      {inputField}
      {addonAfter && <div className={clsx(styles.addon, styles.addonAfter)}>{addonAfter}</div>}
    </div>
  );
});

// ── Input.TextArea ───────────────────────────────────────────────────────────

interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'prefix'> {
  /** Input size variant */
  size?: InputSize;
  /** Validation status */
  status?: InputStatus;
  /** Show character count */
  showCount?: boolean;
  /** Auto-resize to content */
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  /** Element before the textarea */
  prefix?: React.ReactNode;
  /** Element after the textarea */
  suffix?: React.ReactNode;
  /** Disable internal focus border/shadow (when parent handles it) */
  focusEffect?: boolean;
  /** Show border (default true) */
  bordered?: boolean;
  /** Visual style: 'default' | 'glass' | 'underline' — glass shell owns bg, shadow, outline; underline shows only a bottom border */
  variant?: 'default' | 'glass' | 'underline';
  /** Absolutely positioned element inside the glass shell (e.g. send button) */
  action?: React.ReactNode;
  /**
   * Floating label text. Sits inside the field at rest; animates upward and
   * shrinks on focus or when the field has a non-empty value.
   *
   * Works with all variants. Use `placeholder` for a native hint when no
   * floating label is needed.
   */
  label?: string;
}

// Map size → textareaField size class
const textareaFieldSizeClass: Record<InputSize, string> = {
  sm: styles.textareaFieldSm,
  md: styles.textareaFieldMd,
  lg: styles.textareaFieldLg,
};

// Map size → textareaPlain size class
const textareaPlainSizeClass: Record<InputSize, string> = {
  sm: styles.textareaPlainSm,
  md: styles.textareaPlainMd,
  lg: styles.textareaPlainLg,
};

// Map size → textareaAffixWrapper size class
const textareaAffixWrapperSizeClass: Record<InputSize, string> = {
  sm: styles.textareaAffixWrapperSm,
  md: styles.textareaAffixWrapperMd,
  lg: styles.textareaAffixWrapperLg,
};

/**
 * Input.TextArea — multi-line text input.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Font size and padding |
 * | status | 'default' \| 'error' \| 'warning' | 'default' | Border color |
 * | showCount | boolean | false | Character count |
 * | autoSize | boolean \| { minRows, maxRows } | false | Auto-resize |
 * | maxLength | number | — | Max characters |
 *
 * @example
 * <Input.TextArea rows={4} label="Notes" />
 * <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} showCount maxLength={500} />
 */
const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    size = 'md',
    status = 'default',
    showCount,
    autoSize,
    maxLength,
    value,
    defaultValue,
    onChange,
    prefix,
    suffix,
    focusEffect: _focusEffect = true,
    bordered: _bordered = true,
    variant = 'glass',
    action,
    label,
    style,
    disabled,
    rows = 3,
    className,
    ...props
  },
  ref,
) {
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const generatedId = useId();
  const textareaId = (props as { id?: string }).id ?? generatedId;

  const isControlled = value !== undefined;
  const currentValue = isControlled ? String(value) : String(internalValue);
  const hasValue = currentValue.length > 0;
  const labelFloated = focused || hasValue;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) setInternalValue(e.target.value);
    onChange?.(e);

    // Auto-size
    if (autoSize && textareaRef.current) {
      const ta = textareaRef.current;
      ta.style.height = 'auto';
      const minRows = typeof autoSize === 'object' ? (autoSize.minRows ?? 1) : 1;
      const maxRows = typeof autoSize === 'object' ? (autoSize.maxRows ?? Infinity) : Infinity;
      const lineHeight = parseFloat(getComputedStyle(ta).lineHeight) || 20;
      const minH = lineHeight * minRows;
      const maxH = maxRows === Infinity ? Infinity : lineHeight * maxRows;
      ta.style.height = `${Math.min(Math.max(ta.scrollHeight, minH), maxH)}px`;
    }
  };

  const hasAffix = prefix || suffix;

  const labelEl = label !== undefined ? (
    <label
      htmlFor={textareaId}
      className={clsx(
        styles.floatingLabel,
        styles.floatingLabelTextarea,
        labelFloated ? styles.labelFloated : styles.labelRest,
        focused && styles.labelFocused,
        variant === 'glass' && styles.labelGlass,
        variant === 'underline' && styles.labelUnderline,
      )}
    >
      {label}
    </label>
  ) : null;

  if (variant === 'glass') {
    return (
      <div
        data-glass="true"
        className={clsx(styles.glassTextarea, label !== undefined && styles.glassWithLabel, className)}
        style={style}
      >
        {labelEl}
        {prefix && (
          <div className={styles.glassPrefix}>
            {prefix}
          </div>
        )}
        <textarea
          ref={ref ?? textareaRef}
          id={textareaId}
          rows={rows}
          className={clsx(
            styles.textareaField,
            styles.textareaFieldGlass,
            autoSize && styles.textareaFieldAutoSize,
            prefix && styles.textareaFieldGlassHasPrefix,
            action && styles.textareaFieldGlassHasAction,
          )}
          value={isControlled ? value : internalValue}
          onChange={handleChange}
          onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
          disabled={disabled}
          maxLength={maxLength}
          {...props}
        />
        {suffix && (
          <div
            className={clsx(
              styles.glassSuffix,
              action ? styles.glassSuffixWithAction : styles.glassSuffixDefault,
            )}
          >
            {suffix}
          </div>
        )}
        {action && (
          <div className={styles.glassAction}>
            {action}
          </div>
        )}
        {showCount && (
          <span
            className={clsx(
              styles.glassCount,
              action ? styles.glassCountWithAction : styles.glassCountDefault,
            )}
          >
            {currentValue.length}{maxLength ? `/${maxLength}` : ''}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        styles.textareaWrapper,
        variant === 'underline' && styles.wrapperUnderline,
        variant === 'underline' && status === 'error' && styles.wrapperUnderlineError,
        variant === 'underline' && status === 'warning' && styles.wrapperUnderlineWarning,
        label !== undefined && styles.wrapperWithLabel,
        className,
      )}
      style={style}
    >
      {labelEl}
      {hasAffix ? (
        <div
          className={clsx(styles.textareaAffixWrapper, textareaAffixWrapperSizeClass[size])}
        >
          {prefix && (
            <span className={clsx(styles.affix, styles.affixBottom, styles.affixLeft)}>
              {prefix}
            </span>
          )}
          <textarea
            ref={ref ?? textareaRef}
            id={textareaId}
            rows={rows}
            className={clsx(
              styles.textareaField,
              textareaFieldSizeClass[size],
              autoSize && styles.textareaFieldAutoSize,
            )}
            value={isControlled ? value : internalValue}
            onChange={handleChange}
            onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
            onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
            disabled={disabled}
            maxLength={maxLength}
            {...props}
          />
          {suffix && (
            <span className={clsx(styles.affix, styles.affixBottom, styles.affixRight)}>
              {suffix}
            </span>
          )}
        </div>
      ) : (
        <textarea
          ref={ref ?? textareaRef}
          id={textareaId}
          rows={rows}
          className={clsx(
            styles.textareaPlain,
            textareaPlainSizeClass[size],
            autoSize && styles.textareaFieldAutoSize,
            _bordered === false && styles.textareaUnbordered,
            variant === 'underline' && styles.textareaPlainUnderline,
          )}
          value={isControlled ? value : internalValue}
          onChange={handleChange}
          onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
          disabled={disabled}
          maxLength={maxLength}
          {...props}
        />
      )}
      {showCount && (
        <span className={styles.textareaCount}>
          {currentValue.length}
          {maxLength !== undefined ? `/${maxLength}` : ''}
        </span>
      )}
    </div>
  );
});

// ── Input.Password ───────────────────────────────────────────────────────────

interface PasswordProps extends Omit<InputProps, 'type' | 'suffix'> {
  /** Whether visibility toggle is shown */
  visibilityToggle?: boolean;
}

/**
 * Input.Password — password input with visibility toggle.
 *
 * @example
 * <Input.Password label="Password" />
 * <Input.Password visibilityToggle={false} />
 */
const Password = forwardRef<HTMLInputElement, PasswordProps>(function Password(
  { visibilityToggle = true, ...props },
  ref,
) {
  const [visible, setVisible] = useState(false);

  const toggle = visibilityToggle ? (
    <button
      type="button"
      onClick={() => setVisible((v) => !v)}
      className={clsx(styles.clearBtn, styles.passwordToggle)}
      tabIndex={0}
      aria-label={visible ? 'Hide password' : 'Show password'}
      title={visible ? 'Hide password' : 'Show password'}
    >
      {visible ? <Icon name="visibility_off" size={16} /> : <Icon name="visibility" size={16} />}
    </button>
  ) : undefined;

  return <Input ref={ref} {...props} type={visible ? 'text' : 'password'} suffix={toggle} />;
});

// ── Input.Search ─────────────────────────────────────────────────────────────

interface SearchInputProps extends Omit<InputProps, 'suffix'> {
  /** Called when search is triggered (Enter key or button click) */
  onSearch?: (value: string) => void;
}

/**
 * Input.Search — input with search icon and Enter key handler.
 *
 * @example
 * <Input.Search label="Search thoughts" onSearch={handleSearch} allowClear />
 */
const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { onSearch, onKeyDown, ...props },
  ref,
) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.(e.currentTarget.value);
    }
    onKeyDown?.(e);
  };

  return <Input ref={ref} prefix={<Icon name="search" size={16} />} onKeyDown={handleKeyDown} {...props} />;
});

// ── Attach sub-components ────────────────────────────────────────────────────

type InputComponent = typeof Input & {
  TextArea: typeof TextArea;
  Password: typeof Password;
  Search: typeof SearchInput;
};

(Input as InputComponent).TextArea = TextArea;
(Input as InputComponent).Password = Password;
(Input as InputComponent).Search = SearchInput;

export default Input as InputComponent;
export { TextArea, Password, SearchInput };
